import { apiPath } from '../../constants';
import {
    axios,
    apiRequestOptions,
    getImpersonatedUsername,
    stopImpersonation,
    transformHttpResponse,
} from 'utils';

export const SESSION_USER_REFRESHED = 'SESSION_USER_REFRESHED';
export const SESSION_USER_REFRESH_FAILED = 'SESSION_USER_REFRESH_FAILED';

/**
 * How long a page waits for /api/me before trusting the JWT's roles. AuthGuard
 * holds a guarded page until the session is checked, so an unreachable server
 * must not hold it forever — at a venue on a local server that is a real
 * failure mode, and the token still authenticates every real call.
 */
const REFRESH_TIMEOUT_MS = 5000;

/**
 * Reconcile the session with the server's CURRENT view of the user.
 *
 * JWTs snapshot roles at login (24h TTL): after an admin grants a role —
 * e.g. ROLE_SUPER_ADMIN — the holder's UI keeps gating on the stale claim
 * until re-login. Fetching /api/me at app boot makes role changes effective
 * on the next page load; the backend stays the source of truth.
 *
 * AuthGuard waits for this answer (`isSessionChecked`) before it lets a page
 * in or turns it away: deciding on the JWT alone sent a super admin whose role
 * was granted after login to the 401 page whenever they opened /audit-log
 * directly, while the same page opened fine from the menu, by which time the
 * refresh had landed (26 Sep 2026).
 *
 * Failures are deliberately silent (no logout, no error banner): the token
 * itself still authenticates every real API call, and an expired token is
 * already handled by the 401 flow of those calls.
 */
export const refreshSessionUser = () => {
    return function (dispatch) {
        const impersonating = !!getImpersonatedUsername();

        return axios({ ...apiRequestOptions(apiPath.ME.GET), timeout: REFRESH_TIMEOUT_MS })
            .then(transformHttpResponse)
            .then(response => {
                // «Увійти як користувач» the server did not confirm: the page
                // would gate on one person and fetch as another. Stop.
                if (impersonating && !(response.data || {}).impersonator) {
                    stopImpersonation();
                    return undefined;
                }
                return dispatch(sessionUserRefreshed(response.data));
            })
            .catch(() => {
                // A refused switch (403 — a super admin no more, or the user
                // disabled) fails EVERY request; the JWT's roles below would
                // be the super admin's. Back to being themselves.
                if (impersonating) {
                    stopImpersonation();
                    return undefined;
                }
                // Still silent — but the session counts as CHECKED, so
                // AuthGuard stops waiting and gates on the JWT's roles.
                return dispatch({ type: SESSION_USER_REFRESH_FAILED });
            });
    };
};

const sessionUserRefreshed = (data) => {
    return {
        type: SESSION_USER_REFRESHED,
        data,
    };
};

/**
 * «Мої татамі» — the competitions the signed-in user is secretary of and the
 * mats they are assigned to (GET /api/me/tatami-sessions). Resolves null when
 * it cannot be read.
 */
export const fetchMyTatamiSessions = () => () =>
    axios(apiRequestOptions(apiPath.MY_TATAMI_SESSIONS.GET))
        .then(transformHttpResponse)
        .then(response => response.data || null)
        .catch(() => null);
